/**
 * TanStack Query keys for Manpower screens (task 11;
 * `complan-manpower/design.md` section 8).
 *
 * Each of the four screens has its own key factory, following the same pattern
 * Foundation's `calendar-query-keys.ts` establishes: one key per screen/query,
 * scoped by the filter parameters that vary the result, so a filter change
 * re-fetches rather than showing stale data.
 *
 * `ffRole` is typed as `string` rather than `FieldForceRole` because the filter
 * controls carry the raw `<select>` value; the request layer is where it is
 * narrowed, and a key only has to be a stable, serialisable identity.
 */
export const manpowerQueryKeys = {
  /** All Manpower query keys, for invalidating everything at once (task 14.4). */
  all: ['manpower'] as const,

  /** Capacity & Utilisation screen (R7.1); one FF role and Region scope per key. */
  capacityUtilisation: (params: {
    planningYear: number;
    ffRole: string;
    regionId: string;
  }) => ['manpower', 'capacity-utilisation', params] as const,

  /** Demand Explorer screen (R7.2). */
  demandExplorer: (params: {
    planningYear: number;
    granularity: 'month' | 'calendar-week';
    ffRole?: string;
    regionId?: string;
  }) => ['manpower', 'demand-explorer', params] as const,

  /**
   * One Initiative's workload, for the Detail_Panel's Workload tab (R5.8).
   *
   * Under the `manpower` prefix on purpose: it reads the same demand engine as
   * the screens above, so an Effort_Assumption or Capacity_Period write that
   * invalidates {@link all} must refresh this panel too - otherwise the drawer
   * keeps showing hours calculated against a superseded assumption version.
   */
  initiativeWorkload: (params: { initiativeId: string; planningYear: number }) =>
    ['manpower', 'initiative-workload', params] as const,

  /** Capacity Inputs screen - all Capacity_Period records, unpaged (R7.3). */
  capacityPeriods: () => ['manpower', 'capacity-periods'] as const,

  /** Effort Matrix screen - all Effort_Assumption records, unpaged (R7.4). */
  effortAssumptions: () => ['manpower', 'effort-assumptions'] as const,

  /**
   * The Manpower planning assumptions in force (R3.2, R6.4).
   *
   * Under the `manpower` prefix so a Capacity_Period write, which invalidates
   * {@link all}, refreshes them too: a change to an assumption is what makes the
   * capacity figures on screen mean something different, so the two must not be
   * able to disagree about which assumptions produced them.
   */
  planningAssumptions: () => ['manpower', 'planning-assumptions'] as const,

  /**
   * Active Impacted_Population_Count overrides (R4.3).
   *
   * Under the `manpower` prefix because an override changes a driver's population
   * count, which is a demand input: an Initiative write that invalidates
   * {@link all} must refresh this list too, or the Effort Matrix keeps listing an
   * override that has since been withdrawn.
   */
  populationOverrides: () => ['manpower', 'population-overrides'] as const,

  /** Contributing drivers drill-through (R6.5). */
  contributingDrivers: (params: {
    planningYear: number;
    ffRole: string;
    regionId: string;
  }) => ['manpower', 'contributing-drivers', params] as const,
} as const;
